import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  Server,
  Database,
  Shield,
  TrendingUp,
  RefreshCw,
  Bell,
  Cpu,
  HardDrive,
  MemoryStick
} from 'lucide-react';
import { toast } from 'sonner';

const MonitoringDashboard = () => {
  const [monitoringData, setMonitoringData] = useState(null);
  const [systemStatus, setSystemStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testingAlert, setTestingAlert] = useState(false);

  const backendUrl = import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchMonitoringData();
    fetchSystemStatus();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchMonitoringData();
      fetchSystemStatus();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchMonitoringData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${backendUrl}/api/monitoring/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMonitoringData(data);
      }
    } catch (error) {
      console.error('Error fetching monitoring data:', error);
    }
  };

  const fetchSystemStatus = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/monitoring/system-status`);
      if (response.ok) {
        const data = await response.json();
        setSystemStatus(data);
      }
    } catch (error) {
      console.error('Error fetching system status:', error);
    } finally {
      setLoading(false);
    }
  };

  const testAlert = async () => {
    setTestingAlert(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${backendUrl}/api/monitoring/test-alert`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Test alert created successfully!');
        fetchMonitoringData(); // Refresh data
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create test alert');
      }
    } catch (error) {
      console.error('Error creating test alert:', error);
      toast.error('Failed to create test alert');
    } finally {
      setTestingAlert(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-green-600';
      case 'degraded': return 'text-yellow-600';
      case 'unhealthy': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'healthy': return <Badge className="bg-green-100 text-green-800">Healthy</Badge>;
      case 'degraded': return <Badge className="bg-yellow-100 text-yellow-800">Degraded</Badge>;
      case 'unhealthy': return <Badge className="bg-red-100 text-red-800">Unhealthy</Badge>;
      default: return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getResourceColor = (percentage) => {
    if (percentage > 80) return 'text-red-600';
    if (percentage > 60) return 'text-yellow-600';
    return 'text-green-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Loading monitoring data...</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">System Monitoring</h1>
          <p className="text-gray-600 mt-1">Real-time platform health and performance monitoring</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={testAlert}
            disabled={testingAlert}
            variant="outline"
            size="sm"
          >
            {testingAlert ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Bell className="h-4 w-4 mr-2" />
            )}
            Test Alert
          </Button>
          
          <Button 
            onClick={() => {
              fetchMonitoringData();
              fetchSystemStatus();
            }}
            variant="outline"
            size="sm"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* System Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">System Status</p>
                <div className="flex items-center gap-2 mt-1">
                  {systemStatus?.status === 'healthy' ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                  )}
                  {getStatusBadge(systemStatus?.status)}
                </div>
              </div>
              <Server className="h-8 w-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Errors (24h)</p>
                <p className="text-2xl font-bold text-gray-900">
                  {monitoringData?.summary?.error_count_24h || 0}
                </p>
              </div>
              <AlertTriangle className="h-8 w-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg Response Time</p>
                <p className="text-2xl font-bold text-gray-900">
                  {monitoringData?.summary?.avg_response_time_1h || 0}s
                </p>
              </div>
              <Clock className="h-8 w-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Security Events</p>
                <p className="text-2xl font-bold text-gray-900">
                  {monitoringData?.summary?.security_events_24h || 0}
                </p>
              </div>
              <Shield className="h-8 w-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* System Resources */}
      {systemStatus?.system_metrics && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              System Resources
            </CardTitle>
            <CardDescription>Current server resource utilization</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Cpu className="h-6 w-6 text-blue-600" />
                  <div>
                    <p className="font-medium">CPU Usage</p>
                    <p className="text-sm text-gray-600">Processor utilization</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-2xl font-bold ${getResourceColor(systemStatus.system_metrics.cpu_percent)}`}>
                    {systemStatus.system_metrics.cpu_percent.toFixed(1)}%
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <MemoryStick className="h-6 w-6 text-green-600" />
                  <div>
                    <p className="font-medium">Memory Usage</p>
                    <p className="text-sm text-gray-600">RAM utilization</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-2xl font-bold ${getResourceColor(systemStatus.system_metrics.memory_percent)}`}>
                    {systemStatus.system_metrics.memory_percent.toFixed(1)}%
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <HardDrive className="h-6 w-6 text-purple-600" />
                  <div>
                    <p className="font-medium">Disk Usage</p>
                    <p className="text-sm text-gray-600">Storage utilization</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-2xl font-bold ${getResourceColor(systemStatus.system_metrics.disk_percent)}`}>
                    {systemStatus.system_metrics.disk_percent.toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Errors */}
      {monitoringData?.recent_errors && monitoringData.recent_errors.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Recent Errors
            </CardTitle>
            <CardDescription>Latest application errors and exceptions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {monitoringData.recent_errors.map((error, index) => (
                <div key={index} className="border rounded-lg p-4 bg-red-50 border-red-200">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-medium text-red-900">{error.error_type}</h4>
                      <p className="text-sm text-red-700">{error.error_message}</p>
                    </div>
                    <Badge className="bg-red-100 text-red-800">{error.severity}</Badge>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-red-600">
                    <span>{error.request_path} ({error.request_method})</span>
                    <span>{formatTimestamp(error.timestamp)}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Security Events */}
      {monitoringData?.security_events && monitoringData.security_events.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-orange-500" />
              Security Events
            </CardTitle>
            <CardDescription>Recent security-related activities</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {monitoringData.security_events.map((event, index) => (
                <div key={index} className="border rounded-lg p-4 bg-orange-50 border-orange-200">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-medium text-orange-900">{event.event_type}</h4>
                      <p className="text-sm text-orange-700">
                        {JSON.stringify(event.details, null, 2)}
                      </p>
                    </div>
                    <Badge className="bg-orange-100 text-orange-800">{event.severity}</Badge>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-orange-600">
                    <span>IP: {event.ip_address}</span>
                    <span>{formatTimestamp(event.timestamp)}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {(!monitoringData?.recent_errors || monitoringData.recent_errors.length === 0) &&
       (!monitoringData?.security_events || monitoringData.security_events.length === 0) && (
        <Card>
          <CardContent className="text-center py-8">
            <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
            <h3 className="text-lg font-medium mb-2">All Systems Operating Normally</h3>
            <p className="text-gray-600">No recent errors or security events detected.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MonitoringDashboard;